import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { useAuth } from "./app/auth/AuthContext";
import ProtectedRoute from "./app/auth/ProtectedRoute";
import { homePathForRole } from "./app/auth/roles";
import ImpersonationBanner from "./app/components/ImpersonationBanner";

import LoginPage from "./app/pages/LoginPage";
import RegisterPage from "./app/pages/RegisterPage";
import ForgotPasswordPage from "./app/pages/ForgotPasswordPage";
import ResetPasswordPage from "./app/pages/ResetPasswordPage";

import BuyerPortal from "./app/buyer/BuyerPortal";
import BuyerShell from "./app/buyer/BuyerShell";
import BuyerOrdersPage from "./app/buyer/BuyerOrdersPage";
import OrderDetailPage from "./app/orders/OrderDetailPage";

import DistributorLayout from "./app/distributor/DistributorLayout";
import DistributorDashboard from "./app/distributor/DistributorDashboard";
import ProductsPage from "./app/distributor/pages/ProductsPage";
import CategoriesPage from "./app/distributor/pages/CategoriesPage";
import OrdersPage from "./app/distributor/pages/OrdersPage";
import InventoryPage from "./app/distributor/pages/InventoryPage";
import CustomersPage from "./app/distributor/pages/CustomersPage";
import SettingsPage from "./app/distributor/pages/SettingsPage";
import PlaceholderPage from "./app/distributor/pages/PlaceholderPage";

import SuperAdminLayout from "./app/admin/SuperAdminLayout";
import TenantsPage from "./app/admin/TenantsPage";
import TenantDetailPage from "./app/admin/TenantDetailPage";

const RootRedirect: React.FC = () => {
  const { isAuthenticated, role } = useAuth();
  return <Navigate to={isAuthenticated ? homePathForRole(role) : "/login"} replace />;
};

const App: React.FC = () => (
  <BrowserRouter>
    <ImpersonationBanner />
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Buyer portal + dedicated order pages (QA #2) */}
      <Route path="/catalogue" element={<ProtectedRoute allow={["BUYER"]}><BuyerPortal /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute allow={["BUYER"]}><BuyerShell><BuyerOrdersPage /></BuyerShell></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute allow={["BUYER"]}><BuyerShell><OrderDetailPage backPath="/orders" distributorLabel="Food Wholesale" /></BuyerShell></ProtectedRoute>} />

      {/* Distributor portal (shared shell + nested pages) */}
      <Route element={<ProtectedRoute allow={["DISTRIBUTOR"]}><DistributorLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DistributorDashboard />} />
        <Route path="/d/products" element={<ProductsPage />} />
        <Route path="/d/categories" element={<CategoriesPage />} />
        <Route path="/d/orders" element={<OrdersPage />} />
        <Route path="/d/orders/:id" element={<OrderDetailPage manage backPath="/d/orders" />} />
        <Route path="/d/inventory" element={<InventoryPage />} />
        <Route path="/d/customers" element={<CustomersPage />} />
        <Route path="/d/settings" element={<SettingsPage />} />
        <Route path="/d/pricing" element={<PlaceholderPage title="Pricing" blurb="Customer-specific price lists and bulk price overrides will live here." />} />
        <Route path="/d/reports" element={<PlaceholderPage title="Reports" blurb="Sales trends, top products and customer insights will be reported here." />} />
      </Route>

      {/* Platform Super Admin portal (QA #4) */}
      <Route element={<ProtectedRoute allow={["PLATFORM_SUPER_ADMIN"]}><SuperAdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
        <Route path="/admin/tenants" element={<TenantsPage />} />
        <Route path="/admin/tenants/:id" element={<TenantDetailPage />} />
      </Route>

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  </BrowserRouter>
);

export default App;
